import React, { useState } from 'react';
import { guestApi, setGuestSession } from '../../utils/portalApi';

/**
 * Sets or changes the guest's password (guest role).
 *
 * The emailed code stays the way in for everybody, so a password is optional,
 * and forgetting one is fixed by signing in with a code and setting a new one
 * here: straight after a code sign-in the current password is not asked for.
 *
 * Changing it ends every other session the guest has; the reply carries a new
 * one for this browser, which is stored before anything else is asked.
 */
const GuestPassword = ({ me, onSaved, forced = false }) => {
    const [current, setCurrent] = useState('');
    const [next, setNext] = useState('');
    const [repeat, setRepeat] = useState('');
    const [busy, setBusy] = useState(false);
    const [notice, setNotice] = useState({ tone: '', text: '' });

    const askCurrent = Boolean(me.passwordNeedsCurrent);
    const mismatch = repeat && next !== repeat;

    const save = async (e) => {
        e.preventDefault();
        if (next !== repeat) return;
        setBusy(true);
        setNotice({ tone: '', text: '' });
        try {
            const { data } = await guestApi.put('/me/password', {
                currentPassword: askCurrent ? current : undefined,
                newPassword: next
            });
            setGuestSession(data.token);
            setCurrent(''); setNext(''); setRepeat('');
            setNotice({ tone: 'ok', text: me.hasPassword ? 'Password changed. You are signed out everywhere else.' : 'Password set.' });
            onSaved({ ...me, ...data.me });
        } catch (err) {
            setNotice({ tone: 'error', text: err.response?.data?.message || 'Could not save your password.' });
        } finally {
            setBusy(false);
        }
    };

    return (
        <form className="guest-profile" onSubmit={save}>
            {!forced && <h2 className="guest-section-title">{me.hasPassword ? 'Change password' : 'Set a password'}</h2>}
            <p className="guest-muted">
                {forced
                    ? 'Your password was set by the team at GTS. Choose your own to carry on.'
                    : me.hasPassword
                        ? 'You can sign in with your password or with an emailed code.'
                        : 'Optional. With a password you can sign in without waiting for an emailed code.'}
            </p>
            {askCurrent && (
                <label className="portal-label">Current password
                    <input className="ext-input" type="password" autoComplete="current-password" required
                        value={current} onChange={(e) => setCurrent(e.target.value)} />
                </label>
            )}
            <label className="portal-label">New password
                <input className="ext-input" type="password" autoComplete="new-password" required minLength={8} maxLength={128}
                    value={next} onChange={(e) => setNext(e.target.value)} />
            </label>
            <label className="portal-label">Repeat new password
                <input className="ext-input" type="password" autoComplete="new-password" required
                    value={repeat} onChange={(e) => setRepeat(e.target.value)} />
            </label>
            <p className="guest-muted">At least 8 characters, with a letter and a number.</p>
            {mismatch && <p className="portal-error">The two passwords do not match.</p>}
            {notice.text && <p className={notice.tone === 'error' ? 'portal-error' : 'guest-ok'}>{notice.text}</p>}
            <button className="portal-primary" disabled={busy || !next || mismatch || (askCurrent && !current)}>
                {busy ? 'Saving…' : me.hasPassword ? 'Change password' : 'Set password'}
            </button>
        </form>
    );
};

export default GuestPassword;
